import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../services/api";
import ImageUploaderServer from "../components/ImageUploaderServer";

const imgSrc = (url) => (url?.startsWith("http") ? url : `${API_BASE}${url || ""}`);

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    title: "",
    imageUrl: "",
    blurb: "",
    active: true,
  });

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/api/admin/team");
      setItems(data || []);
    } catch (e) {
      setItems([]);
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to manage the team."
          : "Failed to load team."
      );
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.post("/api/admin/team", { ...form, sortOrder: 1000 });
      setForm({ name: "", title: "", imageUrl: "", blurb: "", active: true });
      await load();
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to continue."
          : "Failed to save member."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setErr("");
    try {
      await api.delete(`/api/admin/team/${id}`);
      await load();
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to continue."
          : "Failed to delete member."
      );
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="page space-y-6">
      <section className="card">
        <h1 className="font-display text-3xl md:text-4xl">Team Admin</h1>
        <p className="mt-1 text-slate-300">Manage the team section.</p>
      </section>

      {/* Error */}
      {err && (
        <div className="card bg-red-500/10 border-red-500/20 text-red-200">{err}</div>
      )}

      <section className="card">
        <div className="grid gap-3">
          <input
            className="input"
            placeholder="Name"
            value={form.name}
            onChange={set("name")}
          />
          <input
            className="input"
            placeholder="Title"
            value={form.title}
            onChange={set("title")}
          />

          <ImageUploaderServer
            folder="team"
            value={form.imageUrl}
            onChange={(url) => setForm({ ...form, imageUrl: url })}
          />

          {form.imageUrl && (
            <img
              src={imgSrc(form.imageUrl)}
              alt="Preview"
              className="mt-2 w-full max-w-sm aspect-[4/5] object-cover rounded-xl border border-white/10"
            />
          )}

          <textarea
            className="textarea"
            placeholder="Short blurb"
            value={form.blurb}
            onChange={set("blurb")}
            rows={5}
          />

          <label className="inline-flex items-center gap-2 text-slate-300">
            <input
              type="checkbox"
              className="accent-brand-500"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active
          </label>

          <div>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-3">
        {items.map((it) => (
          <div key={it.id} className="card flex items-center gap-3">
            <div className="shrink-0">
              {it.imageUrl ? (
                <img
                  src={imgSrc(it.imageUrl)}
                  alt={it.name}
                  className="h-16 w-16 rounded-lg object-cover border border-white/10"
                />
              ) : (
                <div className="h-16 w-16 rounded-lg border border-white/10 bg-white/5 grid place-items-center text-[10px] text-slate-400">
                  No image
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-white">{it.name}</div>
              {it.title && <div className="text-xs text-slate-400">{it.title}</div>}
            </div>
            <button className="btn-secondary" onClick={() => remove(it.id)}>
              Delete
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}
